import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePublicSettings } from '@/hooks/usePublicSettings';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { clearNonEssentialCookies } from '@/lib/cookieConsent';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function extractMeasurementIds(code: string): string[] {
  const ids = new Set<string>();
  const idPattern = /\b(?:G|AW|UA)-[A-Z0-9-]+\b/gi;
  for (const match of code.matchAll(idPattern)) {
    ids.add(match[0].toUpperCase());
  }
  return Array.from(ids);
}

function removeManagedTags() {
  document.head
    .querySelectorAll('[data-phx-analytics-managed="true"]')
    .forEach((node) => node.parentNode?.removeChild(node));
}

export function AnalyticsManager() {
  const { data: settings } = usePublicSettings();
  const location = useLocation();
  const analyticsCode = settings?.analyticsCode?.trim() ?? '';
  const measurementIds = useMemo(() => extractMeasurementIds(analyticsCode), [analyticsCode]);
  const injectedCodeRef = useRef<string>('');
  const { granted } = useCookieConsent();

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (!granted || !analyticsCode) {
      removeManagedTags();
      injectedCodeRef.current = '';
      if (!granted) clearNonEssentialCookies();
      return;
    }

    // Already injected this exact snippet — do not tear down tags on settings refetch/remount.
    if (analyticsCode === injectedCodeRef.current) {
      return;
    }

    removeManagedTags();

    const tpl = document.createElement('template');
    tpl.innerHTML = analyticsCode;
    const nodes = Array.from(tpl.content.childNodes);

    nodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const element = node as HTMLElement;

      if (element.tagName.toLowerCase() === 'script') {
        const oldScript = element as HTMLScriptElement;
        const next = document.createElement('script');
        for (const attr of oldScript.attributes) {
          next.setAttribute(attr.name, attr.value);
        }
        next.textContent = oldScript.textContent;
        next.setAttribute('data-phx-analytics-managed', 'true');
        document.head.appendChild(next);
        return;
      }

      const clone = element.cloneNode(true) as HTMLElement;
      clone.setAttribute('data-phx-analytics-managed', 'true');
      document.head.appendChild(clone);
    });

    injectedCodeRef.current = analyticsCode;
  }, [analyticsCode, granted]);

  useEffect(() => {
    if (!granted || !measurementIds.length) return;

    const pagePath = `${location.pathname}${location.search}`;
    const sendPageView = () => {
      if (typeof window.gtag !== 'function') return false;
      measurementIds.forEach((id) => {
        window.gtag?.('config', id, {
          page_path: pagePath,
          page_location: window.location.href,
          page_title: document.title,
        });
      });
      return true;
    };

    if (sendPageView()) return;

    // gtag may not be ready yet on first paint / SPA navigation
    const intervalId = window.setInterval(() => {
      if (sendPageView()) {
        window.clearInterval(intervalId);
      }
    }, 250);
    const timeoutId = window.setTimeout(() => window.clearInterval(intervalId), 10000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [granted, location.pathname, location.search, measurementIds]);

  return null;
}
